// src/components/billing/TransactionListItem.jsx
import React from 'react';
import {
  ListItem, ListItemAvatar, ListItemText, Typography, Box, Tooltip, useTheme, alpha, Stack,
  IconButton
} from '@mui/material';
import {
  RedeemRounded as RedeemIcon, 
  AutorenewRounded as AutorenewIcon, 
  ErrorOutlineRounded as ErrorIcon,
  AccountBalanceWalletRounded as WalletUsageIcon, 
  ShoppingCartRounded as PurchaseIcon, 
  TuneRounded as CreditAdjustmentIcon, // Changed icon for adjustment
  InfoRounded as InfoDetailsIcon,
  ArrowUpwardRounded as PositiveAmountIcon,
  ArrowDownwardRounded as NegativeAmountIcon,
  ReceiptLongRounded as GenericTransactionIcon, 
  Checklist as ListItemIcon,
} from '@mui/icons-material';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  // Example: May 16, 2025, 10:30 AM
  return new Date(dateString).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
  });
};

const getTransactionPresentation = (type = "UNKNOWN_TRANSACTION", theme) => {
  let baseSx = { fontSize: '1.6rem' }; // Adjusted icon size within Avatar
  let colorPalette = theme.palette.grey;
  let label = type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  let IconComponent = GenericTransactionIcon; // Fallback icon

  switch (type.toUpperCase()) {
    case 'SIGNUP_BONUS': 
      IconComponent = RedeemIcon; colorPalette = theme.palette.success; label = "Signup Bonus"; break;
    case 'SUBSCRIPTION_CREDIT_GRANT': 
      IconComponent = AutorenewIcon; colorPalette = theme.palette.primary; label = "Subscription Credits"; break;
    case 'USAGE_DEDUCTION': // Explicitly handle this based on your cURL
      IconComponent = WalletUsageIcon; colorPalette = theme.palette.info; label = "Service Usage"; break;
    case 'ONE_TIME_PURCHASE': // Ensure this matches your API (was ONE_TIME_CREDIT_PURCHASE in cURL)
      IconComponent = PurchaseIcon; colorPalette = theme.palette.secondary; label = "Credit Purchase"; break;
    case 'ONE_TIME_CREDIT_PURCHASE': // Added based on cURL
      IconComponent = PurchaseIcon; colorPalette = theme.palette.secondary; label = "Credit Top-up"; break;
    case 'CREDIT_ADJUSTMENT_MANUAL':
      IconComponent = CreditAdjustmentIcon; colorPalette = theme.palette.warning; label = "Manual Adjustment"; break;
    default:
      IconComponent = ErrorIcon; label = `Unknown: ${label}`; // More informative for unknown types
  }
  return { 
    Icon: <IconComponent sx={{...baseSx, color: colorPalette.dark || colorPalette.main }} />, // Prefer dark variant for icon color if available
    label, 
    bgColor: alpha(colorPalette.main, 0.08), // Lighter background
    borderColor: alpha(colorPalette.main, 0.25)
  };
};

const TransactionListItem = ({ transaction, onDetailsClick }) => {
  const theme = useTheme();
  if (!transaction || !transaction.id) {
    return (
        <ListItem divider sx={{py:2, borderRadius: '12px', my:0.5, bgcolor: alpha(theme.palette.error.main, 0.05)}}>
            <ListItemIcon sx={{minWidth: 56, mr: {xs:1, sm:1.5}}}>
                <Box sx={{width: 44, height: 44, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(theme.palette.error.main, 0.1)}}>
                    <ErrorIcon sx={{fontSize: '1.75rem', color: theme.palette.error.main}}/>
                </Box>
            </ListItemIcon>
            <ListItemText primary="Transaction Data Corrupted" primaryTypographyProps={{color: theme.palette.error.dark, fontWeight:500}} secondary="Please contact support if this issue persists."/>
        </ListItem>
    );
  }
  
  const presentation = getTransactionPresentation(transaction.transaction_type, theme);
  
  const amount = parseFloat(transaction.amount);
  const isAmountValid = !isNaN(amount);
  const isPositive = isAmountValid && amount > 0;
  const isNegative = isAmountValid && amount < 0;

  const amountColor = isPositive ? theme.palette.success.dark : (isNegative ? theme.palette.error.dark : theme.palette.text.secondary);
  const AmountIcon = isPositive ? PositiveAmountIcon : (isNegative ? NegativeAmountIcon : null);

  return (
    <ListItem 
      divider={false} // Remove default divider, border on item itself
      sx={{ 
        py: {xs:1.5, sm:2}, px: {xs:1, sm:2}, 
        my: 0.75, // Space between items
        borderRadius: '16px', // More rounded
        backgroundColor: theme.palette.background.paper, // Use paper for slight elevation feel
        border: `1px solid ${presentation.borderColor || theme.palette.divider}`,
        boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.03)}`,
        transition: theme.transitions.create(['transform', 'box-shadow', 'border-color'], {duration: theme.transitions.duration.short}),
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 12px ${alpha(presentation.borderColor || theme.palette.primary.main, 0.15)}`,
            borderColor: presentation.borderColor ? alpha(presentation.borderColor, 0.8) : theme.palette.primary.light,
        }
      }}
      secondaryAction={onDetailsClick && ( // Not implementing onDetailsClick for now, but placeholder is good
        <Tooltip title="View Details (coming soon)">
            <IconButton edge="end" aria-label="details" onClick={() => onDetailsClick(transaction.id)} disabled>
                <InfoDetailsIcon />
            </IconButton>
        </Tooltip>
      )}
    >
      <ListItemAvatar sx={{minWidth: {xs:52, sm:56}, mr: {xs:1.5, sm:2}}}>
        <Box sx={{
            width: {xs:40, sm:44}, height: {xs:40, sm:44}, borderRadius: '12px', // Slightly more rounded avatar
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: presentation.bgColor,
            // border: `1px solid ${presentation.borderColor}` // Border on main item is enough
        }}>
            {presentation.Icon}
        </Box>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography variant="body1" fontWeight={500} component="div" noWrap title={presentation.label}>
            {presentation.label}
          </Typography>
        }
        secondary={
          <Typography variant="caption" color="text.secondary" component="div" noWrap title={transaction.description || `Tx ID: ${transaction.id}`}>
            {transaction.description ? (transaction.description.length > 60 ? transaction.description.substring(0, 57) + "..." : transaction.description) : `ID: ${transaction.id.slice(0,12)}...`}
          </Typography>
        }
        sx={{my:0, flexGrow: 1, pr: onDetailsClick ? 1 : 0}}
      />
      <Stack sx={{ textAlign: 'right', minWidth: {xs: 110, sm:150}, ml: 'auto', alignItems:'flex-end', justifyContent:'center', pl:1 }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', color: amountColor, display:'flex', alignItems:'center', gap: 0.5, fontSize: {xs: '1rem', sm:'1.125rem'} }}>
          {AmountIcon && <AmountIcon sx={{fontSize:'1.2rem', color: amountColor}}/>}
          {isAmountValid ? `${isPositive ? '+' : ''}${amount.toFixed(amount % 1 === 0 ? 0 : (Math.abs(amount) < 0.001 ? 6 : (Math.abs(amount) < 1 ? 4 : 2)))}` : 'N/A'}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{fontSize: '0.8rem'}}>
          {formatDate(transaction.created_at)}
        </Typography>
        {transaction.balance_after_transaction !== null && transaction.balance_after_transaction !== undefined && (
          <Tooltip title="Balance after this transaction">
            <Typography variant="caption" color="text.disabled" display="block" sx={{fontSize: '0.75rem', mt:0.25}}>
              Bal: {parseFloat(transaction.balance_after_transaction).toFixed(2)}
            </Typography>
          </Tooltip>
        )}
      </Stack>
    </ListItem>
  );
};

export default TransactionListItem;